import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import {
  CurrentRecruiterForProfileFinishDocument,
  UpdateRecruiterInput,
  useCurrentRecruiterForProfileFinishQuery,
  useUpdateRecruiterMutation,
  useUpdateRecruiterPasswordMutation,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { closeModal } from '@src/redux/actions/modal';
import { ProfileData } from './ProfileData';
import { Password } from './Password';
import { Step } from './enums/step.enum';
import './styles.scss';

export const FinishProfileModal: FC = () => {
  const dispatch = useAppDispatch();

  const [currentStep, setCurrentStep] = useState(Step.DATA);
  const [updateRecruiterInput, setUpdateRecruiterInput] = useState<UpdateRecruiterInput>({});
  const [newPassword, setNewPassword] = useState<string>('');

  const { data: recruiterData } = useCurrentRecruiterForProfileFinishQuery({
    fetchPolicy: 'no-cache',
    onCompleted: ({ currentRecruiter }) => {
      setUpdateRecruiterInput({
        countryId: currentRecruiter.country?.id,
        fullName: currentRecruiter.fullName,
        phoneNumber: currentRecruiter.phone,
        positionTitle: currentRecruiter.title,
      });

      if (currentRecruiter.isIncomplete) {
        setCurrentStep(Step.DATA);
      } else if (currentRecruiter.isDraft) {
        setCurrentStep(Step.PASSWORD);
      } else {
        dispatch(closeModal());
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
  });

  const [updateRecruiter] = useUpdateRecruiterMutation({
    onCompleted: ({ updateRecruiter }) => {
      if (updateRecruiter.isDraft) {
        setCurrentStep(Step.PASSWORD);
      } else {
        dispatch(setSuccessFlashMessage('Profile successfully updated.'));
        dispatch(closeModal());
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
    refetchQueries: [CurrentRecruiterForProfileFinishDocument],
  });

  const [updateRecruiterPassword, { loading: isUpdateRecruiterPasswordLoading }] = useUpdateRecruiterPasswordMutation({
    onCompleted: () => {
      dispatch(setSuccessFlashMessage('Profile successfully updated.'));

      dispatch(closeModal());
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
    refetchQueries: [CurrentRecruiterForProfileFinishDocument],
  });

  const recruiter = recruiterData?.currentRecruiter;

  const onContinue = async () => {
    if (currentStep === Step.DATA) {
      await updateRecruiter({
        variables: {
          updateInput: updateRecruiterInput,
        },
      });
    } else if (currentStep === Step.PASSWORD) {
      await updateRecruiterPassword({
        variables: {
          newPassword: newPassword,
        },
      });
    }
  };

  if (!recruiter) {
    return null;
  }

  return (
    <div className={classNames('finish-recruiter-modal', currentStep)}>
      {currentStep === Step.DATA ? (
        <ProfileData
          onContinue={onContinue}
          onUpdateRecruiterInput={setUpdateRecruiterInput}
          recruiter={recruiter}
          updateRecruiterInput={updateRecruiterInput}
        />
      ) : (
        <Password
          isUpdateRecruiterPasswordLoading={isUpdateRecruiterPasswordLoading}
          newPassword={newPassword}
          onBack={() => setCurrentStep(Step.DATA)}
          onContinue={onContinue}
          onUpdateNewPassword={setNewPassword}
        />
      )}
    </div>
  );
};
